import { useState } from "react";
import { toast } from "sonner";

import { TASK_URGENCIES } from "@handoverpro/shared";
import type { TaskUrgency } from "@handoverpro/shared";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { PatientResponseDto } from "~/lib/api";
import { addTask } from "~/lib/patients";

const URGENCY_LABELS: Record<TaskUrgency, string> = {
  urgent: "Urgent",
  soon: "Soon",
  routine: "Routine",
};

export interface AddTaskFormProps {
  sessionId: string;
  patientId: string;
  /** Called with the full refreshed patient when a task is successfully added. */
  onAdded: (patient: PatientResponseDto) => void;
}

/**
 * Inline add-task form for the patient detail screen (Task 15). Renders a
 * description input + urgency Select; on submit calls `addTask` and lifts the
 * refreshed patient up via `onAdded`. Errors are surfaced as toasts.
 */
export function AddTaskForm({ sessionId, patientId, onAdded }: AddTaskFormProps): JSX.Element {
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState<TaskUrgency>("routine");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = description.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const result = await addTask(sessionId, patientId, trimmed, urgency);
      if (result.status === "ok") {
        setDescription("");
        setUrgency("routine");
        onAdded(result.patient);
      } else if (result.status === "error") {
        toast.error(result.message);
      } else {
        toast.error("Could not add task. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-2 rounded-lg border border-border p-3"
    >
      <Input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Task description"
        aria-label="Task description"
        maxLength={500}
        disabled={submitting}
      />
      <div className="flex items-center gap-2">
        <Select
          value={urgency}
          onValueChange={(v) => setUrgency(v as TaskUrgency)}
          disabled={submitting}
        >
          <SelectTrigger aria-label="Task urgency" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TASK_URGENCIES.map((u) => (
              <SelectItem key={u} value={u}>
                {URGENCY_LABELS[u]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" disabled={submitting || !description.trim()}>
          Add task
        </Button>
      </div>
    </form>
  );
}
